import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ListChecks,
  Pencil,
  Star,
  Target,
  User,
  Users,
  Waypoints,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  useToast,
} from '../../ui';
import { useWorkstream } from '../../features/workstreams/useWorkstreams';
import {
  formatDate,
  isFollowing,
  stageTone,
} from '../../features/workstreams/workstreamModel';
import { WORKSTREAM_STAGES, stageLabel } from '../../features/workstreams/workstreamConfig';
import { WorkstreamModal } from './WorkstreamModal';

/** A labeled block of narrative text (hidden when empty). */
function Field({ label, value }: { label: string; value?: string }) {
  if (!value || !value.trim()) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-300">{value}</p>
    </div>
  );
}

/** A labeled list of chips (hidden when empty). */
function ChipList({ label, items }: { label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((i) => (
          <Badge key={i} tone="neutral">
            {i}
          </Badge>
        ))}
      </div>
    </div>
  );
}

/** A labeled list of narrative bullets (hidden when empty). */
function BulletList({ label, items }: { label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-300">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Workstream detail — answers, for one workstream: who owns it, why, what is
 * changing, the systems affected, the technical approach, expected impact, the
 * current stage, the linked sprint stories and goal, whether it's blocked
 * (dependencies), and who to contact. Offers a FOLLOW action, an inline stage
 * change, and an edit modal.
 */
export function WorkstreamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    workstream,
    goal,
    allGoals,
    stories,
    currentAlias,
    loading,
    error,
    save,
    toggleFollow,
    setStage,
    structureFromNotes,
  } = useWorkstream(id);

  const [editOpen, setEditOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading workstream" />
      </div>
    );
  }

  if (error || !workstream) {
    return (
      <div>
        <PageHeader
          icon={<Waypoints className="h-5 w-5" />}
          title="Workstream"
          actions={
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/workstreams')}
            >
              Back to Workstreams
            </Button>
          }
        />
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load workstream"
          description={error ?? 'This workstream does not exist or was deleted.'}
        />
      </div>
    );
  }

  const following = isFollowing(workstream.followers, currentAlias);
  const followerCount = workstream.followers.length;

  const onFollow = async () => {
    await toggleFollow();
    toast({
      tone: 'success',
      title: following ? 'Unfollowed' : 'Following',
      description: workstream.title,
    });
  };

  const onChangeStage = async (stage: string) => {
    const saved = await setStage(stage);
    toast({ tone: 'success', title: 'Stage updated', description: stageLabel(saved.currentStage) });
  };

  const onSave = async (draft: Parameters<typeof save>[0]) => {
    const saved = await save(draft);
    toast({ tone: 'success', title: 'Workstream saved', description: saved.title });
    return saved;
  };

  return (
    <div>
      <PageHeader
        icon={<Waypoints className="h-5 w-5" />}
        title={workstream.title}
        description={
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Badge tone={stageTone(workstream.currentStage)} dot>
              {stageLabel(workstream.currentStage)}
            </Badge>
            <span className="text-zinc-600">·</span>
            <span className="inline-flex items-center gap-1">
              <User className="h-3.5 w-3.5" /> {workstream.engineer || '—'}
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/workstreams')}
            >
              Back
            </Button>
            <Button
              variant={following ? 'secondary' : 'outline'}
              leftIcon={
                <Star className={`h-4 w-4 ${following ? 'fill-amber-300 text-amber-300' : ''}`} />
              }
              onClick={onFollow}
            >
              {following ? 'Following' : 'Follow'}
            </Button>
            <Button leftIcon={<Pencil className="h-4 w-4" />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardBody className="space-y-4">
              <Field label="Why" value={workstream.why} />
              <Field label="What is changing" value={workstream.whatChanging} />
              <Field label="Technical approach" value={workstream.technicalApproach} />
              <ChipList label="Systems affected" items={workstream.systemsAffected} />
              <BulletList label="Expected impact" items={workstream.expectedImpact} />
              <BulletList
                label="Dependencies / blocked on"
                items={workstream.dependencies}
              />
            </CardBody>
          </Card>

          {/* Linked sprint stories */}
          <Card>
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <ListChecks className="h-4 w-4" /> Sprint stories (
                  {stories.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardBody>
              {stories.length === 0 ? (
                <p className="text-sm text-zinc-500">
                  No sprint stories linked. Add Task ids on the workstream to
                  trace the engineering work.
                </p>
              ) : (
                <ul className="divide-y divide-surface-border rounded-xl border border-surface-border">
                  {stories.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-sm text-zinc-200">
                        {s.title}
                      </span>
                      {s.resolved ? (
                        <Badge tone="success">
                          <CheckCircle2 className="h-3 w-3" /> linked
                        </Badge>
                      ) : (
                        <Badge tone="warning">unresolved id</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Sidebar column */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Stage</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <Select
                label="Current stage"
                value={workstream.currentStage}
                options={WORKSTREAM_STAGES.map((s) => ({
                  value: s.value,
                  label: s.label,
                }))}
                onChange={(e) => void onChangeStage(e.target.value)}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact & goal</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm text-zinc-400">
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-500">
                  Point of contact
                </p>
                <p className="mt-0.5 inline-flex items-center gap-1 text-zinc-200">
                  <User className="h-3.5 w-3.5" /> {workstream.poc || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-zinc-500">
                  Linked goal
                </p>
                {goal ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/goals/${encodeURIComponent(goal.id)}`)}
                    className="mt-0.5 inline-flex items-center gap-1 text-sm text-accent-fg hover:underline"
                  >
                    <Target className="h-3.5 w-3.5" /> {goal.title}
                  </button>
                ) : (
                  <p className="mt-0.5 text-zinc-500">Not linked to a goal</p>
                )}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <Users className="h-4 w-4" /> Followers ({followerCount})
                </span>
              </CardTitle>
            </CardHeader>
            <CardBody>
              {followerCount === 0 ? (
                <p className="text-sm text-zinc-500">
                  No followers yet. Follow to get context on this work.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {workstream.followers.map((f) => (
                    <Badge key={f} tone="neutral">
                      {f}
                    </Badge>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm text-zinc-400">
              <p>
                Created by{' '}
                <span className="text-zinc-200">{workstream.createdBy || '—'}</span>
              </p>
              <p>Last updated {formatDate(workstream.lastUpdated) || '—'}</p>
            </CardBody>
          </Card>
        </div>
      </div>

      <WorkstreamModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        workstream={workstream}
        goals={allGoals}
        defaultEngineer={currentAlias}
        onCreate={onSave}
        onSave={onSave}
        onStructure={structureFromNotes}
      />
    </div>
  );
}

export default WorkstreamDetailPage;
